import React, { useEffect, useMemo, useState } from 'react';
import API, { CDN_ROOT } from './API';
import { TitleProvider, useTitle, useUser } from './Hooks';
import { SidebarItem, SidebarUrl } from './Classes';
import { Await, Link, useLoaderData, useLocation, useParams, useRouteError } from 'react-router-dom';
import { bindClick } from '@tempu/components/Util';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Button, Header, Icon, Image } from 'react-frontier';
import { useNavigate } from 'react-router-dom';
import Onboarding from './screens/onboarding/Onboarding';
import similarity from 'string-similarity';
import classNames from 'classnames';

interface ContainerProps{
	outlet?: JSX.Element,
	items: SidebarItem[],
	returnUrl?: string | SidebarUrl,
	error?: boolean,
	login?: boolean,
	loaderElement?: JSX.Element,
	onboarded?: boolean
}

var InnerTitle = (props: { onMenuClick: ()=>void })=>{
	var { subtitle, action } = useTitle();
	return <>
		<div className="menu" onClick={props.onMenuClick}>
			<i className="bars icon"></i>
		</div>
		<div className="left">{subtitle}</div>
		<div className="right">{action}</div>
	</>;
}

var SiteContainer = (props: ContainerProps)=>{
	var [scrolled, setScrolled] = useState<boolean>(false);
	var [sidemenu, setSidemenu] = useState<boolean>(false);
	var { user, logged_in, logout } = useUser();
	var error = useRouteError();
	var location = useLocation();
	var params = useParams();
	var loader = useLoaderData();

	useEffect(()=>{
		setSidemenu(false);
	}, [location.pathname]);

	var login = ()=>{
		window.location.href = API.getAuthUrl();
	}

	const BEST_MATCH_THREASHOLD = 0.5;
	var active_index = useMemo(()=>{
		if(!props.items) return -1;
		var sidebar_paths : string[][] = props.items.map(a=>{
			if(!a.path) return ['------------'];
			var paths : string[] = [];
			
			if(typeof a.path==='string') paths.push((a.path as string));
			else paths.push(a.path(location.pathname, params));

			if(a.active_paths && a.active_paths.length>0){
				for(var p of a.active_paths){
					if(typeof p==='string') paths.push(p as string);
					else paths.push((p as SidebarUrl)(location.pathname, params));
				}
			}
			return paths;
		});

		var ix = -1, ix_best = -1;
		for(var i=0; i<sidebar_paths.length; i++){
			var sim = similarity.findBestMatch(location.pathname, sidebar_paths[i]);
			if(sim.bestMatch && sim.bestMatch.rating>BEST_MATCH_THREASHOLD && sim.bestMatch.rating>ix_best){
				ix = i;
				ix_best = sim.bestMatch.rating;
			}
		}

		return ix;
	}, [location.pathname]);

	var showMenu = ()=>{
		setSidemenu(!sidemenu);
	}

	if(user && !user.onboarded){
		return <Onboarding />
	}

	const SIDEBAR_CONTENTS = (
		<div className='container'>
			<Link className={classNames('user', { logged: !logged_in })} onClick={!logged_in ? login : null} to={logged_in ? '/profile' : API.getAuthUrl()}>
				<div className="avatar">
					{(logged_in && user.image) ? (
						<Image src={user.image} fallbackComponent={(
							<i className='user icon' />
						)} />
					) : (
						<i className={classNames('icon', {
							'sign in': !logged_in,
							'user': logged_in
						})}></i>
					)}
				</div>
				<div className="info">
					{logged_in && user ? <>
						<div className="name">{user.first_name} {user.last_name}</div>
						<div className="email">{user.email}</div>
					</> : (
						<div className="login">Iniciar sesión</div>
					)}
				</div>
			</Link>
			{!!props.returnUrl && <>
				<Link className='item return' to={typeof props.returnUrl === 'string' ? props.returnUrl : (props.returnUrl as SidebarUrl)(location.pathname, params)}>
					<i className="arrow left icon"></i>
					<div className="text">Regresar</div>
				</Link>
				<div className="divider"></div>
			</>}
			{props.items.map((a, i)=>{
				if(a.login===true && !logged_in) return null;
				if(a.admin===true && !user?.admin) return null;
				var url = a.path ? (typeof a.path === 'string' ? (a.path as string) : (a.path as SidebarUrl)(location.pathname, params)) : null;
				var key = a.key || (`SBR-${url || ('itm-'+i)}`);

				if(a.type==='label'){
					return <div className="label" key={key}>
						{a.text}
					</div>
				}else if(a.type==='divider'){
					return <div className="divider" key={key}></div>
				}else if(a.type==='element'){
					return <React.Fragment key={key}>
						{a.element(location.pathname, params)}
					</React.Fragment>
				}else if(url){
					return <Link className={classNames('item', { active: active_index===i })} to={url} key={key}>
						<Icon name={a.icon} />
						<div className="text">{a.text}</div>
					</Link>
				}else 
				return null;
			})}
		</div>
	)
	
	return <TitleProvider suffix='Ebentu'>
		<div className="tp site-container">
			<div className="sidebar">
				<div className="logo">
					<img src={`${CDN_ROOT}/logo/LogoRH_128.webp`} />
				</div>
				{SIDEBAR_CONTENTS}
				<div className="spacer"></div>
				{!!logged_in && (
					<div className="item" style={{ marginBottom: 0, color: 'gray' }} onClick={()=>logout(true)}>
						<i className="sign out icon"></i>
						<div className="text">Cerrar sesión</div>
					</div>
				)}
			</div>
			<div className={classNames('content', { scrolled })}>
				<div className="header">
					<InnerTitle onMenuClick={showMenu} />
				</div>
				<div className="content">
					{(props.login && !logged_in) ? (
						<Header text='Iniciar sesión' subtext='La página que deseas accesar requiere iniciar sesión' iconName='sign-in' actions={(
							<Button as={Link} to={API.getAuthUrl()} text='Iniciar sesión' style={{ width: 200 }} color='red' />
						)} />
					) : error ? (
						<Header
							text={(error as any).status || 404}
							subtext={(!(error as any).statusText || (error as any).status===404) ? 'Página no encontrada' : (error as any).statusText} 
							style={{ fontSize: 100 }}
							subheaderStyle={{ fontSize: 20 }}
						/>
					) : loader && (loader as any).data ? (
						<React.Suspense
							fallback={props.loaderElement || (
								<Header loading text='Cargando' />
							)}
						>
							<Await resolve={(loader as any)?.data}>
								{props.outlet}
							</Await>
						</React.Suspense>
					) : props.outlet}
				</div>
				<TransitionGroup component={React.Fragment}>
					{!!sidemenu && (
						<CSSTransition enter addEndListener={null} timeout={{ appear: 0, exit: 500 }}>
							<div className="overlay" onClick={bindClick(setSidemenu, false)}></div>
						</CSSTransition>
					)}
				</TransitionGroup>
			</div>
		</div>
		<TransitionGroup component={React.Fragment}>
			{!!sidemenu && (
				<CSSTransition enter addEndListener={null} timeout={{ appear: 0, exit: 500 }}>
					<div className="tp sidemenu">
						{SIDEBAR_CONTENTS}
					</div>
				</CSSTransition>
			)}
		</TransitionGroup>
	</TitleProvider>
}

export default SiteContainer;